import logger from '../log';
import { getUTMParams, type UTMParams } from './index';

const STORAGE_KEY = 'storedUTMParams';
const EXPIRY_DAYS = 1;

interface StoredUTMData {
  utmParams: UTMParams;
  timestamp: number;
}

/**
 * Captures UTM parameters from the current URL and stores them (last-touch attribution).
 * Always overwrites existing UTMs with the latest from the URL.
 */
export function captureAndStoreUTMParams(): void {
  if (typeof window === 'undefined') return;

  try {
    // Get UTMs from current URL
    const utmParams = getUTMParams();
    if (!utmParams || Object.keys(utmParams).length === 0) {
      // No UTMs in URL, nothing to store
      return;
    }

    // Store UTMs with timestamp (last-touch: always overwrite)
    const storedData: StoredUTMData = {
      utmParams,
      timestamp: Date.now(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(storedData));
  } catch (error) {
    logger.error('Failed to capture and store UTM parameters:', error instanceof Error ? error.message : String(error));
  }
}

/**
 * Retrieves stored UTM parameters from localStorage.
 * Returns null if expired, not found, or on server-side.
 */
export function getStoredUTMParams(): UTMParams | null {
  if (typeof window === 'undefined') return null;

  try {
    const storedValue = localStorage.getItem(STORAGE_KEY);
    if (!storedValue) {
      return null;
    }

    const data: StoredUTMData = JSON.parse(storedValue);
    const expiryInMs = EXPIRY_DAYS * 24 * 60 * 60 * 1000;

    // Check if expired
    if (Date.now() - data.timestamp > expiryInMs) {
      clearStoredUTMParams();
      return null;
    }

    return data.utmParams;
  } catch (error) {
    logger.error(
      'Failed to load stored UTM parameters from localStorage:',
      error instanceof Error ? error.message : String(error),
    );
    return null;
  }
}

/**
 * Clears stored UTM parameters from localStorage.
 * Called after form submission to reset attribution for next conversion.
 */
export function clearStoredUTMParams(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    logger.error(
      'Failed to clear stored UTM parameters from localStorage:',
      error instanceof Error ? error.message : String(error),
    );
  }
}
